import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
    Paper,
    Box,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    Button,
    IconButton,
    Popover,
    MenuItem,
    TextField,
    Tooltip, // keep MUI Tooltip
    useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ReferenceLine,
    ReferenceArea,
} from 'recharts';
import FactorSelectDropdown from './FactorSelectDropdown';
import EmptyPerformanceChart from './EmptyPerformanceChart';
import PerformanceWindowControls from './PerformanceWindowControls';
import SecurityBreakoutPopout from './SecurityBreakoutPopout';
import { PORTFOLIO_HISTORY_CONTRACT, isSnapshotExposureHistory, isPortfolioAttribution, factorColor } from './currentAttribution';
import { chartFactorKey as factorKey, rankFactors, latestFactorValueForSeries, buildPerformanceChartData } from './performanceChartData';
import {
    DECOMPOSITION_MODE_CONFIG,
    DECOMPOSITION_MODE_EXECUTION,
    DECOMPOSITION_MODE_REALIZATION,
    DECOMPOSITION_MODE_RISK,
    normalizeDecompositionMode,
} from './decompositionModes';
import { PERFORMANCE_DECOMPOSITION_CHART_HEIGHT } from './performanceUiConstants';
import { computeWindowSummaryMetrics } from './windowSummaryMetrics';
import PlotReturnSummary from './PlotReturnSummary';
import {
    getPerformanceChartChrome,
    getPerformanceTooltipStyle,
    performanceCompactLabelSx,
    performanceDragMarkerSx,
    performanceInsetSx,
    performancePanelSx,
    performancePopoverPaperSx,
    performanceSecondaryControlSx,
    performanceToggleButtonSx,
    performanceToggleGroupSx,
} from '../styles/performanceMsiStyles';

const ELEVATED_CARD_SX = {
    position: 'relative',
    borderRadius: '6px',
    borderTop: '1px solid rgba(226, 232, 240, 0.98)',
    borderLeft: '1px solid rgba(226, 232, 240, 0.98)',
    borderRight: '1px solid rgba(148, 163, 184, 0.56)',
    borderBottom: '3px solid rgba(100, 116, 139, 0.34)',
    boxShadow:
        'inset 0 1px 0 rgba(255, 255, 255, 0.92), 4px 4px 0 rgba(30, 41, 59, 0.44), 4px 4px 0 1px rgba(148, 163, 184, 0.16), 0 12px 20px rgba(0, 0, 0, 0.22)',
};

// Toggle styles
const toggleBtnSx = {
    color: '#eee',
    px: 1.2,
    '&.Mui-selected': {
        bgcolor: '#11161c',
        color: '#fff',
        borderColor: '#777',
    },
    '&.Mui-selected:hover': { bgcolor: '#1a232d' },
    ...performanceToggleButtonSx,
};

const parseISO = (s) => new Date(`${s}T00:00:00Z`);

// ---------- formatting helpers ----------
function formatValue(val, mode) {
    if (val == null || isNaN(val)) return '-';
    if (mode === 'percent') return (val * 100).toFixed(2) + '%';
    return '$' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function formatMetric(label, val) {
    if (val == null || isNaN(val)) return '-';
    if (label === 'hit_rate') return (val * 100).toFixed(0) + '%';
    return Number(val).toFixed(2);
}
function formatCurrencyTick(value) {
    if (value == null || Number.isNaN(Number(value))) return '-';
    const n = Number(value);
    const abs = Math.abs(n);
    if (abs >= 1e12) return `${n < 0 ? '-' : ''}${(abs / 1e12).toFixed(1)}T`;
    if (abs >= 1e9) return `${n < 0 ? '-' : ''}${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${n < 0 ? '-' : ''}${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${n < 0 ? '-' : ''}${(abs / 1e3).toFixed(1)}K`;
    return `${Math.round(n)}`;
}
function hasReturnSeries(metrics, component, capability, seriesKey) {
    return (isPortfolioAttribution(metrics) ? metrics?.capabilities?.[capability]?.available === true : metrics?.capabilities?.[capability]?.available !== false)
        && Object.values(metrics?.returns?.[component]?.[seriesKey] || {}).some(Number.isFinite);
}
function DecompositionTooltip({ active, label, payload, valueMode, formatLabel, factorView, theme, availableSeries, attributionMode, selectedFactors = [], getFactorColor, reportingCurrency, portfolioLabel = 'Current portfolio', leftLabel = 'Factor', rightLabel = 'Idiosyncratic' }) {
    if (!active || !payload || payload.length === 0) return null;
    const rowPayload = payload[0]?.payload || {};
    const rows = [
        {
            key: 'actual_factor',
            name: attributionMode ? `${portfolioLabel} ${leftLabel}` : 'Actual Factor',
            color: '#60a5fa',
            value: factorView === 'by_factor'
                ? rowPayload.actual_factor_total
                : rowPayload.actual_factor,
        },
        {
            key: 'actual_idio',
            name: attributionMode ? `${portfolioLabel} ${rightLabel}` : 'Actual Idio',
            color: '#1d4ed8',
            value: rowPayload.actual_idio,
        },
        {
            key: 'actual_total',
            name: attributionMode ? `${portfolioLabel} Total` : 'Actual Total',
            color: '#bfdbfe',
            value: rowPayload.actual_total,
        },
    ];
    if (availableSeries.actual_adjustment) rows.push({ key: 'actual_adjustment', name: 'Vendor-return adjustment', color: '#a78bfa', value: rowPayload.actual_adjustment });
    if (attributionMode && factorView === 'by_factor') {
        selectedFactors.forEach((factor) => rows.push({ key: factorKey(factor), name: factor, color: getFactorColor(factor), value: rowPayload[factorKey(factor)] }));
        rows.push({ key: 'actual_other_factors', name: 'Other factors', color: '#78909c', value: rowPayload.actual_other_factors });
    }
    return (
        <div style={getPerformanceTooltipStyle(theme)}>
            <div style={{ color: getPerformanceChartChrome(theme).text, marginBottom: 4 }}>
                Date: {formatLabel ? formatLabel(label) : label}
            </div>
            <div style={{ display: 'grid', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                {rows.filter((row) => availableSeries[row.key] || (attributionMode && factorView === 'by_factor' && (row.key === 'actual_other_factors' || selectedFactors.some((f) => factorKey(f) === row.key)))).map((row) => {
                    const color = row.color || row.fill || '#ccc';
                    const name  = row.name || row.dataKey;
                    const val   = row.value;
                    return (
                        <div key={String(row.key || row.dataKey || row.name)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 10, height: 10, background: color, borderRadius: '50%' }} />
                            <span style={{ color: getPerformanceChartChrome(theme).secondaryText }}>{name}</span>
                            <span style={{ color: getPerformanceChartChrome(theme).text, marginLeft: 'auto' }}>
                                {valueMode === 'dollar' && attributionMode ? `${reportingCurrency || ''} ${Number.isFinite(val) ? Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }) : 'Unavailable'}` : formatValue(val, valueMode)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

 const fmtMonth = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yy = d.getUTCFullYear();
    return `${mm}/${yy}`;
};

const inferPeriodicity = (dates) => {
    if (!dates || dates.length < 2) return 'daily';
    const day = 86400000;
    const diffs = [];
    for (let i = 1; i < dates.length; i++) {
        const a = parseISO(dates[i - 1]).getTime();
        const b = parseISO(dates[i]).getTime();
        diffs.push(Math.round((b - a) / day));
    }
    diffs.sort((x, y) => x - y);
    const med = diffs[Math.floor(diffs.length / 2)];
    if (med >= 28) return 'monthly';
    if (med >= 6)  return 'weekly';
    return 'daily';
};

export default function PerformancePlot({
    actualPerformanceMetrics,
    lookback = '1Y',
    onLookbackChange = () => {},
    decompositionMode = DECOMPOSITION_MODE_RISK,
    onDecompositionModeChange = null,
    securityBreakoutPayload = null,
    securityBreakoutLoading = false,
    securityBreakoutAsOfDate = null,
    securityBreakoutGroupLabel = null,
    securityBreakoutStartDate = null,
    securityBreakoutEndDate = null,
    onOpenSecurityBreakout = null,
    onCloseSecurityBreakout = null,
    allowSecurityBreakout = true,
    attributionMode = false,
    analysisSource = null,
    analysisBasis = null,
    inspectorOpen = false,
    attributionDetail = null,
    attributionDetailError = null,
    identityRows = {},
    identityDisplayField = null,
    onIdentityDisplayFieldChange = null,
    inspectorSelectedFactors = null,
    factorDetailFocus = null,
    onFactorSelectionChange = null,
    windowEndCap = null,
    showWindowControls = true,
    historyLoading = false,
    empty = false,
    active = true,
}) {
    const theme = useTheme();
    const chartChrome = getPerformanceChartChrome(theme);
    const currentAttribution = attributionMode || isPortfolioAttribution(actualPerformanceMetrics);
    const sharedHistory = actualPerformanceMetrics?.calculation_contract === PORTFOLIO_HISTORY_CONTRACT || analysisBasis !== null;
    const portfolioLabel = sharedHistory ? 'Portfolio' : currentAttribution ? 'Current portfolio' : 'Actual';
    const reportingCurrency = actualPerformanceMetrics?.denominator?.reporting_currency || null;
    const currencyAvailable = !currentAttribution || actualPerformanceMetrics?.capabilities?.currency_display?.available === true;
    // plot toggles
    const [valueMode, setValueMode] = useState('percent');
    const [seriesMode, setSeriesMode] = useState('cumulative');
    const [factorView, setFactorView] = useState('total');
    const [showSecurityBreakout, setShowSecurityBreakout] = useState(false);
    const [inspectorContext, setInspectorContext] = useState(null);
    const inspectorScope = JSON.stringify([analysisBasis, analysisSource?.fixture_id || null, analysisSource?.generation || null, lookback, decompositionMode]);
    const [selectedSecurityBreakoutTimestamp, setSelectedSecurityBreakoutTimestamp] = useState(null);
    const [selectedSecurityBreakoutRange, setSelectedSecurityBreakoutRange] = useState({ start: null, end: null });
    const [dragStartDate, setDragStartDate] = useState(null);
    const [dragCurrentDate, setDragCurrentDate] = useState(null);
    const [dragStartMarker, setDragStartMarker] = useState(null);
    const [dragCurrentMarker, setDragCurrentMarker] = useState(null);
    const [dataReadinessAnchorEl, setDataReadinessAnchorEl] = useState(null);

    useEffect(() => {
        if (!active || !currentAttribution) return;
        setShowSecurityBreakout(false);
        setSelectedSecurityBreakoutTimestamp(null);
        setSelectedSecurityBreakoutRange({ start: null, end: null });
        setDragStartDate(null);
        setDragCurrentDate(null);
        setDragStartMarker(null);
        setDragCurrentMarker(null);
    }, [active, currentAttribution, lookback, analysisBasis, analysisSource?.fixture_id, analysisSource?.generation, decompositionMode]);

    // factor selections
    const [selectedFactors, setSelectedFactors] = useState([]);
    const [hasCustomSelection, setHasCustomSelection] = useState(false);
    const [factorFamily, setFactorFamily] = useState('all');
    const factorSummaries = actualPerformanceMetrics?.contribution_summary?.by_factor || [];
    const factorFamilies = [...new Set(factorSummaries.map((row) => row.family).filter(Boolean))];
    const requestedDecompositionMode = normalizeDecompositionMode(decompositionMode, DECOMPOSITION_MODE_RISK);
    const tradingAvailable = !sharedHistory || !actualPerformanceMetrics || ['trading', 'non_trading'].every((key) => actualPerformanceMetrics.capabilities?.[key]?.available === true);
    const realizationAvailable = !sharedHistory || !actualPerformanceMetrics || ['realized', 'unrealized', 'adjustment'].every((key) => actualPerformanceMetrics.capabilities?.[key]?.available === true);
    const resolvedDecompositionMode = (requestedDecompositionMode === DECOMPOSITION_MODE_EXECUTION && !tradingAvailable)
        || (requestedDecompositionMode === DECOMPOSITION_MODE_REALIZATION && !realizationAvailable)
        ? DECOMPOSITION_MODE_RISK : requestedDecompositionMode;

    const modelView = !sharedHistory || resolvedDecompositionMode === DECOMPOSITION_MODE_RISK;
    const componentConfig = DECOMPOSITION_MODE_CONFIG[sharedHistory ? resolvedDecompositionMode : DECOMPOSITION_MODE_RISK];
    const leftKey = componentConfig.leftKey;
    const rightKey = componentConfig.rightKey;
    const leftLabel = componentConfig.leftLabel;
    const residualLabel = modelView && currentAttribution ? 'Idiosyncratic' : componentConfig.rightLabel;
    const effectiveFactorView = modelView ? factorView : 'total';

    // extract data blocks (define BEFORE using them anywhere)
    const aRet = actualPerformanceMetrics?.returns;

    const dateList = useMemo(() => aRet?.dates || [], [aRet?.dates]);
    const snapshotInspection = currentAttribution && actualPerformanceMetrics?.capabilities?.exposure_snapshot?.available === true;
    const inspectorStart = dateList[0] || (snapshotInspection ? actualPerformanceMetrics?.window?.start_date : null);
    const inspectorEnd = dateList.at(-1) || (snapshotInspection ? actualPerformanceMetrics?.window?.end_date : null);
    const latestDate = dateList.length ? dateList[dateList.length - 1] : null;
    const firstChartDate = dateList.length ? dateList[0] : null;
    useEffect(() => {
        if (!active || !inspectorOpen || !inspectorStart || !inspectorEnd) return;
        setSelectedSecurityBreakoutTimestamp(inspectorEnd);
        setSelectedSecurityBreakoutRange(inspectorStart !== inspectorEnd
            ? { start: inspectorStart, end: inspectorEnd }
            : { start: null, end: null });
        setInspectorContext(inspectorScope);
        setShowSecurityBreakout(true);
    }, [active, inspectorOpen, factorDetailFocus, inspectorStart, inspectorEnd, inspectorScope]);
    const activeSecurityBreakoutTimestamp = useMemo(() => {
        if (!dateList.length) return null;
        if (
            selectedSecurityBreakoutTimestamp
            && dateList.includes(selectedSecurityBreakoutTimestamp)
        ) {
            return selectedSecurityBreakoutTimestamp;
        }
        return dateList[dateList.length - 1];
    }, [dateList, selectedSecurityBreakoutTimestamp]);

    const dataReadinessPopoverOpen = Boolean(dataReadinessAnchorEl);
    const dataReadiness = actualPerformanceMetrics?.data_readiness || null;
    const dataReadinessIsLimited = Boolean(dataReadiness?.is_window_limited);
    const firstComputableDate = String(dataReadiness?.first_computable_date || '').trim();
    const dataReadinessUserMessage = String(dataReadiness?.user_message || '').trim();
    const dataReadinessDeveloperMessage = String(dataReadiness?.developer_message || '').trim();
    const dataReadinessWindowStart = String(dataReadiness?.window_start_date || '').trim();
    const dataReadinessWindowEnd = String(dataReadiness?.window_end_date || '').trim();
    const dataReadinessFirstExecutionDate = String(dataReadiness?.first_execution_date || '').trim();
    const dataReadinessFirstPriceDate = String(dataReadiness?.first_price_date || '').trim();
    const dataReadinessBlockingReasons = Array.isArray(dataReadiness?.blocking_reasons)
        ? dataReadiness.blocking_reasons
        : [];
    const dataReadinessLogKey = useMemo(() => ([
        dataReadinessWindowStart,
        dataReadinessWindowEnd,
        firstComputableDate,
        dataReadinessDeveloperMessage,
    ].join('|')), [
        dataReadinessDeveloperMessage,
        dataReadinessWindowEnd,
        dataReadinessWindowStart,
        firstComputableDate,
    ]);
    useEffect(() => {
        if (!active || !dataReadinessIsLimited) return;
        // eslint-disable-next-line no-console
        console.info('[HistoricalPerformance][DataReadiness]', dataReadinessLogKey, dataReadiness);
    }, [active, dataReadiness, dataReadinessIsLimited, dataReadinessLogKey]);

    const periodicity = useMemo(() => {
        return (
            actualPerformanceMetrics?.returns?.frequency ||
            inferPeriodicity(dateList)
        );
    }, [actualPerformanceMetrics, dateList]);

    const formatTick = React.useCallback((iso) => {
        if (!iso) return '';
        return periodicity === 'monthly' ? fmtMonth(iso) : iso;
    }, [periodicity]);

    // derive series key
    const seriesKey = useMemo(() => {
        const scale = valueMode === 'percent' ? 'pct' : 'dollar';
        const accum = seriesMode === 'cumulative' ? 'cumulative' : 'absolute';
        return `${accum}_${scale}`;
    }, [valueMode, seriesMode]);

    const hasByFactorAny = Object.keys(aRet?.by_factor || {}).length > 0;
    const orderedFactors = useMemo(() => rankFactors({
        byFactor: aRet?.by_factor, seriesKey, dateList,
    }), [aRet?.by_factor, seriesKey, dateList]);
    const factorsForFamily = (family) => family === 'all' ? orderedFactors
        : orderedFactors.filter((factor) => factorSummaries.find((row) => row.factor === factor)?.family === family);
    const selectableFactors = factorsForFamily(factorFamily);

    const getFactorColor = (factor) => factorColor(factor);

    useEffect(() => {
        if (!active) return;
        if (!hasByFactorAny) {
            if (currentAttribution) return;
            if (selectedFactors.length) setSelectedFactors([]);
            return;
        }
        const intersected = selectedFactors.filter((f) => orderedFactors.includes(f));
        if (!hasCustomSelection) {
            setSelectedFactors(orderedFactors.slice(0, 5));
            if (currentAttribution) setHasCustomSelection(true);
        } else if (intersected.length !== selectedFactors.length) {
            setSelectedFactors(intersected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, orderedFactors, seriesKey, dateList, hasByFactorAny]);

    useEffect(() => {
        if (active && currentAttribution && typeof onFactorSelectionChange === 'function') onFactorSelectionChange(effectiveFactorView === 'by_factor' ? selectedFactors : []);
    }, [active, currentAttribution, effectiveFactorView, selectedFactors, onFactorSelectionChange]);

    useEffect(() => {
        if (active && currentAttribution && actualPerformanceMetrics && !currencyAvailable) setValueMode('percent');
    }, [active, currentAttribution, currencyAvailable, actualPerformanceMetrics]);

    const combined = useMemo(() => buildPerformanceChartData({
        returns: aRet, dateList, seriesKey, leftKey, rightKey,
        byFactor: effectiveFactorView === 'by_factor' && hasByFactorAny,
        selectedFactors, includeOtherFactors: currentAttribution,
        includeAdjustment: sharedHistory && resolvedDecompositionMode === DECOMPOSITION_MODE_REALIZATION,
    }), [aRet, dateList, seriesKey, leftKey, rightKey, effectiveFactorView, hasByFactorAny,
        selectedFactors, currentAttribution, sharedHistory, resolvedDecompositionMode]);
    const showDataReadinessMarker = Boolean(
        dataReadinessIsLimited
        && firstComputableDate
        && combined.some((row) => row.date === firstComputableDate)
    );

    // A missing component is unavailable, not a measured zero return.
    const showActualTotal = hasReturnSeries(actualPerformanceMetrics, 'total', 'total_return', seriesKey);
    const showActualFactor = hasReturnSeries(actualPerformanceMetrics, leftKey, modelView ? 'factor_contribution' : leftKey, seriesKey);
    const showActualIdio = hasReturnSeries(actualPerformanceMetrics, rightKey, modelView ? 'idiosyncratic_return' : rightKey, seriesKey);
    const showAdjustment = sharedHistory && resolvedDecompositionMode === DECOMPOSITION_MODE_REALIZATION && hasReturnSeries(actualPerformanceMetrics, 'adjustment', 'adjustment', seriesKey);
    const factorLayerEnabled = showActualFactor;
    const windowTotalPercentage = hasReturnSeries(actualPerformanceMetrics, 'total', 'total_return', 'cumulative_pct')
        ? aRet?.total?.cumulative_pct?.[latestDate] : null;
    const windowTotalAmount = currencyAvailable && hasReturnSeries(actualPerformanceMetrics, 'total', 'total_return', 'cumulative_dollar')
        ? aRet?.total?.cumulative_dollar?.[latestDate] : null;

    // order factor stack around 0
    const selectedFactorsOrderedBySign = useMemo(() => {
        if (!selectedFactors.length) return [];
        const scored = selectedFactors.map((f) => {
            const v = latestFactorValueForSeries({ returns: aRet, factor: f, seriesKey, dateList });
            return { f, v: Number(v) || 0 };
        });
        const positives = scored.filter(s => s.v > 0).sort((a,b)=>b.v - a.v);
        const negatives = scored.filter(s => s.v < 0).sort((a,b)=>a.v - b.v);
        const zeros     = scored.filter(s => s.v === 0);
        return [...positives, ...negatives, ...zeros].map(s => s.f);
    }, [selectedFactors, aRet, seriesKey, dateList]);

    // stack ids & bar widths
    const actualStackId = useMemo(() => {
        const parts = ['a', showActualIdio ? 'I' : 'i', showActualFactor ? 'F' : 'f', effectiveFactorView];
        if (effectiveFactorView === 'by_factor') parts.push(selectedFactors.join(','));
        return parts.join('_');
    }, [showActualIdio, showActualFactor, effectiveFactorView, selectedFactors]);
    const chartBoxRef = useRef(null);
    const [chartW, setChartW] = useState(0);

    useEffect(() => {
        const el = chartBoxRef.current;
        if (!active || !el) return;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) setChartW(e.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [active]);

    const barSizeDynamic = useMemo(() => {
        const nCategories = Math.max(dateList.length, 1);
        const innerW = Math.max(chartW - 100, 200);
        const MAX_BAR = 48;
        const MIN_BAR = 2;
        const ideal = Math.floor(innerW / nCategories);
        return Math.max(MIN_BAR, Math.min(MAX_BAR, ideal));
        }, [chartW, dateList.length]);

    useEffect(() => {
        if (!active) return;
        if (!dateList.length) {
            if (snapshotInspection) return;
            if (selectedSecurityBreakoutTimestamp !== null) {
                setSelectedSecurityBreakoutTimestamp(null);
            }
            if (selectedSecurityBreakoutRange.start || selectedSecurityBreakoutRange.end) {
                setSelectedSecurityBreakoutRange({ start: null, end: null });
            }
            return;
        }
        if (
            selectedSecurityBreakoutTimestamp
            && dateList.includes(selectedSecurityBreakoutTimestamp)
        ) {
            return;
        }
        setSelectedSecurityBreakoutTimestamp(dateList[dateList.length - 1]);
    }, [active, dateList, snapshotInspection, selectedSecurityBreakoutRange.end, selectedSecurityBreakoutRange.start, selectedSecurityBreakoutTimestamp]);

    useEffect(() => {
        if (!active) return;
        const rangeStart = selectedSecurityBreakoutRange.start;
        const rangeEnd = selectedSecurityBreakoutRange.end;
        if (!rangeStart || !rangeEnd || (!dateList.length && snapshotInspection)) return;
        if (!dateList.includes(rangeStart) || !dateList.includes(rangeEnd)) {
            setSelectedSecurityBreakoutRange({ start: null, end: null });
        }
    }, [active, dateList, snapshotInspection, selectedSecurityBreakoutRange.end, selectedSecurityBreakoutRange.start]);

    const extractDateFromChartEvent = useCallback((event) => {
        const payloadDate = String(event?.activePayload?.[0]?.payload?.date || '').trim();
        if (payloadDate && dateList.includes(payloadDate)) return payloadDate;
        const activeLabel = String(event?.activeLabel || '').trim();
        if (activeLabel && dateList.includes(activeLabel)) return activeLabel;
        return null;
    }, [dateList]);

    const normalizeDateRange = useCallback((firstDate, secondDate) => {
        if (!firstDate || !secondDate) return null;
        if (firstDate <= secondDate) {
            return { start: firstDate, end: secondDate };
        }
        return { start: secondDate, end: firstDate };
    }, []);
    const buildDragMarker = useCallback((event, date) => {
        if (!date) return null;
        const x = Number(event?.chartX);
        const y = Number(event?.chartY);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        return {
            x,
            y,
            label: formatTick(date),
        };
    }, [formatTick]);

    const dragPreviewRange = useMemo(
        () => normalizeDateRange(dragStartDate, dragCurrentDate),
        [dragCurrentDate, dragStartDate, normalizeDateRange]
    );
    const committedRange = useMemo(() => {
        const rangeStart = selectedSecurityBreakoutRange.start;
        const rangeEnd = selectedSecurityBreakoutRange.end;
        if (!rangeStart || !rangeEnd) return null;
        return normalizeDateRange(rangeStart, rangeEnd);
    }, [normalizeDateRange, selectedSecurityBreakoutRange.end, selectedSecurityBreakoutRange.start]);
    const chartHighlightedRange = dragPreviewRange || committedRange;
    const popupSummaryMetrics = useMemo(() => currentAttribution ? null : computeWindowSummaryMetrics({
        returns: aRet,
        mode: resolvedDecompositionMode,
        selectedTimestamp: activeSecurityBreakoutTimestamp,
        selectedRangeStart: committedRange?.start || null,
        selectedRangeEnd: committedRange?.end || null,
        seriesMode,
    }), [
        aRet,
        activeSecurityBreakoutTimestamp,
        committedRange?.end,
        committedRange?.start,
        resolvedDecompositionMode,
        seriesMode,
    ]);
    const inspector = actualPerformanceMetrics?.inspector || {};
    const recentExecutionsRaw = Array.isArray(inspector?.recent_executions) ? inspector.recent_executions : [];
    const recentExecutions = useMemo(() => {
        if (committedRange?.start && committedRange?.end && committedRange.start !== committedRange.end) {
            return recentExecutionsRaw.filter((row) => {
                const rowTimestamp = String(row?.bucket_timestamp || '');
                if (!rowTimestamp) return false;
                return rowTimestamp >= committedRange.start && rowTimestamp <= committedRange.end;
            });
        }
        if (!activeSecurityBreakoutTimestamp) return recentExecutionsRaw;
        if (seriesMode === 'absolute') {
            return recentExecutionsRaw.filter((row) => row?.bucket_timestamp === activeSecurityBreakoutTimestamp);
        }
        const activeMillis = Date.parse(String(activeSecurityBreakoutTimestamp));
        return recentExecutionsRaw.filter((row) => {
            const rowTimestamp = String(row?.bucket_timestamp || '');
            if (!rowTimestamp) return false;
            const rowMillis = Date.parse(rowTimestamp);
            if (Number.isFinite(activeMillis) && Number.isFinite(rowMillis)) {
                return rowMillis <= activeMillis;
            }
            return rowTimestamp <= String(activeSecurityBreakoutTimestamp);
        });
    }, [activeSecurityBreakoutTimestamp, committedRange, recentExecutionsRaw, seriesMode]);
    const noExecutionsMessage = committedRange?.start && committedRange?.end && committedRange.start !== committedRange.end
        ? 'No trades found for the selected range.'
        : (
            seriesMode === 'cumulative'
                ? 'No trades found up to the selected point.'
                : 'No trades found for the selected day.'
        );
    const executionContextPayload = useMemo(() => ({
        selectedTimestamp: activeSecurityBreakoutTimestamp,
        noExecutionsMessage,
        recentExecutions,
    }), [activeSecurityBreakoutTimestamp, noExecutionsMessage, recentExecutions]);

    const handleChartMouseDown = useCallback((event) => {
        if (!allowSecurityBreakout) return;
        const date = extractDateFromChartEvent(event);
        if (!date) return;
        setDragStartDate(date);
        setDragCurrentDate(null);
        setDragStartMarker(buildDragMarker(event, date));
        setDragCurrentMarker(null);
    }, [allowSecurityBreakout, buildDragMarker, extractDateFromChartEvent]);

    const handleChartMouseMove = useCallback((event) => {
        if (!dragStartDate) return;
        const date = extractDateFromChartEvent(event);
        if (!date) return;
        setDragCurrentDate(date);
        setDragCurrentMarker(buildDragMarker(event, date));
    }, [buildDragMarker, dragStartDate, extractDateFromChartEvent]);

    const handleChartMouseUp = useCallback((event) => {
        if (!dragStartDate) return;
        const releaseDate = extractDateFromChartEvent(event) || dragCurrentDate || dragStartDate;
        const nextRange = normalizeDateRange(dragStartDate, releaseDate);
        if (nextRange) {
            const hasSpan = nextRange.start !== nextRange.end;
            setSelectedSecurityBreakoutRange(hasSpan ? nextRange : { start: null, end: null });
            setSelectedSecurityBreakoutTimestamp(nextRange.end);
            if (typeof onOpenSecurityBreakout === 'function') {
                if (currentAttribution) {
                    onOpenSecurityBreakout({ start_date: hasSpan || seriesMode === 'absolute' ? nextRange.start : firstChartDate, end_date: nextRange.end });
                } else if (hasSpan && firstChartDate && nextRange.start !== firstChartDate) {
                    onOpenSecurityBreakout({ lookback: `${nextRange.start}..${nextRange.end}` });
                } else {
                    onOpenSecurityBreakout();
                }
            }
            setInspectorContext(inspectorScope);
        setShowSecurityBreakout(true);
        }
        setDragStartDate(null);
        setDragCurrentDate(null);
        setDragStartMarker(null);
        setDragCurrentMarker(null);
    }, [dragCurrentDate, dragStartDate, extractDateFromChartEvent, firstChartDate, normalizeDateRange, onOpenSecurityBreakout, currentAttribution, seriesMode, inspectorScope]);

    const handleChartMouseLeave = useCallback(() => {
        if (!dragStartDate) return;
        setDragStartDate(null);
        setDragCurrentDate(null);
        setDragStartMarker(null);
        setDragCurrentMarker(null);
    }, [active, dragStartDate]);

    useEffect(() => {
        if (!active || !dragStartDate) return undefined;
        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            setDragStartDate(null);
            setDragCurrentDate(null);
            setDragStartMarker(null);
            setDragCurrentMarker(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dragStartDate]);

    const openFullWindowSecurityBreakout = useCallback(() => {
        const start = inspectorStart;
        const end = inspectorEnd;
        if (!start || !end) return;
        setSelectedSecurityBreakoutTimestamp(end);
        setSelectedSecurityBreakoutRange(
            start !== end
                ? { start, end }
                : { start: null, end: null }
        );
        if (typeof onOpenSecurityBreakout === 'function') {
            onOpenSecurityBreakout(currentAttribution ? { start_date: start, end_date: end } : undefined);
        }
        setInspectorContext(inspectorScope);
        setShowSecurityBreakout(true);
    }, [inspectorStart, inspectorEnd, onOpenSecurityBreakout, currentAttribution, inspectorScope]);

    useEffect(() => {
        if (active) return;
        setShowSecurityBreakout(false); setDataReadinessAnchorEl(null);
        setDragStartDate(null); setDragCurrentDate(null);
        setDragStartMarker(null); setDragCurrentMarker(null);
    }, [active]);
    if (!active) return null;

    return (
        <Paper
            elevation={1}
            sx={{
                ...ELEVATED_CARD_SX,
                p: 2,
                mt: 0,
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#000' : theme.palette.background.paper),
                color: 'text.primary',
                overflow: 'hidden',
                ...performancePanelSx,
            }}
        >
            <Box sx={{ display: 'grid', gap: 1.1, mb: 1.25, width: '100%' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    {(!currentAttribution || sharedHistory) && typeof onDecompositionModeChange === 'function' && (
                        <ToggleButtonGroup
                            value={resolvedDecompositionMode}
                            exclusive
                            onChange={(_, value) => value && onDecompositionModeChange(value)}
                            size="small"
                            sx={{ border: '1px solid #555', bgcolor: '#000', height: 32, ...performanceToggleGroupSx }}
                        >
                            <ToggleButton value={DECOMPOSITION_MODE_RISK} sx={toggleBtnSx}>{sharedHistory ? 'Factor attribution' : 'Risk'}</ToggleButton>
                            <ToggleButton value={DECOMPOSITION_MODE_EXECUTION} disabled={!tradingAvailable} sx={toggleBtnSx}>Trading</ToggleButton>
                            <ToggleButton value={DECOMPOSITION_MODE_REALIZATION} disabled={!realizationAvailable} sx={toggleBtnSx}>Realization</ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    <ToggleButtonGroup
                        value={valueMode}
                        exclusive
                        onChange={(_, v) => v && setValueMode(v)}
                        size="small"
                        sx={{ border: '1px solid #555', bgcolor: '#000', height: 32, ...performanceToggleGroupSx }}
                    >
                        <ToggleButton value="percent" sx={{ ...toggleBtnSx, px: 1.5 }}>%</ToggleButton>
                        <ToggleButton value="dollar" disabled={!currencyAvailable} sx={{ ...toggleBtnSx, px: 1.5 }}>{currentAttribution ? reportingCurrency || 'Currency unavailable' : '$'}</ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        value={seriesMode}
                        exclusive
                        onChange={(_, v) => v && setSeriesMode(v)}
                        size="small"
                        sx={{ border: '1px solid #555', bgcolor: '#000', height: 32, ...performanceToggleGroupSx }}
                    >
                        <ToggleButton value="cumulative" sx={toggleBtnSx}>{currentAttribution ? 'Cumulative contribution' : 'Cumulative'}</ToggleButton>
                        <ToggleButton value="absolute" sx={toggleBtnSx}>{currentAttribution ? 'Daily' : 'Absolute'}</ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        value={factorView}
                        exclusive
                        onChange={(_, v) => v && setFactorView(v)}
                        size="small"
                        disabled={!modelView || !factorLayerEnabled}
                        sx={{ border: '1px solid #555', bgcolor: '#000', height: 32, ...performanceToggleGroupSx }}
                    >
                        <ToggleButton value="total" disabled={!modelView || !factorLayerEnabled} sx={toggleBtnSx}>{currentAttribution ? 'Factor total' : 'Factor: Total'}</ToggleButton>
                        <ToggleButton value="by_factor" sx={toggleBtnSx} disabled={!modelView || !hasByFactorAny}>
                            {currentAttribution ? 'By factor' : 'Factor: By factor'}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {effectiveFactorView === 'by_factor' && factorLayerEnabled && hasByFactorAny && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid #5d6a78',
                                bgcolor: '#000',
                                ...performanceInsetSx,
                            }}
                        >
                            {currentAttribution && factorFamilies.length > 1 && <TextField select size="small" label="Factor family" value={factorFamily} onChange={(event) => {
                                const family = event.target.value; setFactorFamily(family); setHasCustomSelection(true);
                                setSelectedFactors(factorsForFamily(family).slice(0, family === 'all' ? 5 : 20));
                            }} sx={{ minWidth: 145 }}><MenuItem value="all">All families</MenuItem>{factorFamilies.map((family) => <MenuItem key={family} value={family}>{family}</MenuItem>)}</TextField>}
                            <FactorSelectDropdown
                                factors={selectableFactors}
                                selected={selectedFactors}
                                onChange={(next) => { setHasCustomSelection(true); setSelectedFactors(next.slice(0, 20)); }}
                                getColor={getFactorColor}
                                buttonLabel="Select factors"
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => { setHasCustomSelection(true); setSelectedFactors(selectableFactors.slice(0, 5)); }}
                                sx={{ height: 32, ...performanceSecondaryControlSx }}
                            >
                                Top 5
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => { setHasCustomSelection(true); setSelectedFactors(selectableFactors.slice(0, 10)); }}
                                sx={{ height: 32, ...performanceSecondaryControlSx }}
                            >
                                Top 10
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => { setHasCustomSelection(true); setSelectedFactors(selectableFactors.slice(0, 20)); }}
                                sx={{ height: 32, ...performanceSecondaryControlSx }}
                            >
                                Top 20
                            </Button>
                        </Box>
                    )}

                    {showWindowControls && <PerformanceWindowControls lookback={lookback} onLookbackChange={onLookbackChange}
                        currentAttribution={currentAttribution} latestDate={latestDate}
                        windowEndCap={windowEndCap || actualPerformanceMetrics?.window?.supported_end_date}
                        requestedStartDate={actualPerformanceMetrics?.window?.requested_start_date}
                        requestedEndDate={actualPerformanceMetrics?.window?.requested_end_date}>
                        {dataReadinessIsLimited && (
                            <Tooltip title="Data availability details" placement="top" arrow>
                                <IconButton
                                    size="small"
                                    onClick={(event) => setDataReadinessAnchorEl(event.currentTarget)}
                                    sx={{
                                        color: '#facc15',
                                        border: '1px solid rgba(250, 204, 21, 0.55)',
                                        borderRadius: 1,
                                        p: 0.55,
                                        '&:hover': {
                                            borderColor: 'rgba(250, 204, 21, 0.85)',
                                            bgcolor: 'rgba(250, 204, 21, 0.08)',
                                        },
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </PerformanceWindowControls>}

                    {allowSecurityBreakout && <Button
                        variant="outlined"
                        size="small"
                        onClick={openFullWindowSecurityBreakout}
                        sx={{
                            height: 32,
                            ...performanceSecondaryControlSx,
                        }}
                    >
                        {currentAttribution ? isSnapshotExposureHistory(actualPerformanceMetrics) ? 'Snapshot exposures' : 'Contributors' : 'Security Returns'}
                    </Button>}
                    {currentAttribution && !historyLoading && <PlotReturnSummary label="Window total"
                        percentage={windowTotalPercentage} amount={windowTotalAmount} currency={reportingCurrency}
                        color={theme.palette.mode === 'dark' ? '#bfdbfe' : '#1d4ed8'} />}
                </Box>

                <Popover
                    open={dataReadinessPopoverOpen}
                    anchorEl={dataReadinessAnchorEl}
                    onClose={() => setDataReadinessAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 0.6,
                                p: 1.2,
                                minWidth: 360,
                                maxWidth: 520,
                                bgcolor: '#000',
                                color: '#e5e7eb',
                                border: '1px solid rgba(148, 163, 184, 0.55)',
                                display: 'grid',
                                gap: 0.8,
                                ...performancePopoverPaperSx,
                            },
                        },
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>
                        Historical Data Availability
                    </Typography>
                    {dataReadinessUserMessage ? (
                        <Typography variant="caption" color="text.secondary">
                            {dataReadinessUserMessage}
                        </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                        {`Window: ${dataReadinessWindowStart || '-'} -> ${dataReadinessWindowEnd || '-'}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {`First execution: ${dataReadinessFirstExecutionDate || '-'} | First price: ${dataReadinessFirstPriceDate || '-'} | First fully computable: ${firstComputableDate || '-'}`}
                    </Typography>
                    {dataReadinessBlockingReasons.length ? (
                        <Box sx={{ display: 'grid', gap: 0.5, mt: 0.2 }}>
                            {dataReadinessBlockingReasons.map((item) => (
                                <Typography
                                    key={`${String(item?.reason || '')}-${String(item?.affected_instrument_count || 0)}`}
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                >
                                    {`${String(item?.message || item?.reason || '').trim()} (${Number(item?.affected_instrument_count || 0)} instrument${Number(item?.affected_instrument_count || 0) === 1 ? '' : 's'})`}
                                </Typography>
                            ))}
                        </Box>
                    ) : null}
                </Popover>
            </Box>

            <SecurityBreakoutPopout
                open={showSecurityBreakout && (!sharedHistory || inspectorContext === inspectorScope)}
                onClose={() => {
                    setShowSecurityBreakout(false);
                    setSelectedSecurityBreakoutRange({ start: null, end: null });
                    if (typeof onCloseSecurityBreakout === 'function') onCloseSecurityBreakout();
                }}
                groupLabel={securityBreakoutGroupLabel}
                windowStartDate={securityBreakoutStartDate}
                windowEndDate={securityBreakoutEndDate}
                securityBreakout={securityBreakoutPayload}
                seriesMode={seriesMode}
                loading={securityBreakoutLoading}
                selectedTimestamp={activeSecurityBreakoutTimestamp}
                selectedRangeStart={committedRange?.start || null}
                selectedRangeEnd={committedRange?.end || null}
                asOfDate={securityBreakoutAsOfDate || latestDate}
                executionContext={executionContextPayload}
                summaryMetrics={currentAttribution ? null : popupSummaryMetrics}
                attributionMode={currentAttribution}
                attributionDecompositionMode={resolvedDecompositionMode}
                analysisSource={analysisSource || actualPerformanceMetrics?.source} analysisBasis={analysisBasis}
                attributionDetail={attributionDetail}
                attributionAnalysisBasis={actualPerformanceMetrics?.analysis_basis}
                attributionDetailError={attributionDetailError}
                factorDetailFocus={factorDetailFocus}
                identityRows={identityRows} identityDisplayField={identityDisplayField} onIdentityDisplayFieldChange={onIdentityDisplayFieldChange}
                reportingCurrency={currencyAvailable ? reportingCurrency : null}
                selectedFactors={inspectorSelectedFactors || (effectiveFactorView === 'by_factor' ? selectedFactors : [])}
            />

            {/* Chart */}
            <Box
                ref={chartBoxRef}
                sx={{
                    width: '100%',
                    height: PERFORMANCE_DECOMPOSITION_CHART_HEIGHT,
                    position: 'relative',
                    '& .recharts-bar-rectangle, & .recharts-bar-rectangle path, & .recharts-bar-rectangle .recharts-rectangle': {
                        cursor: 'pointer',
                    },
                }}
            >
                {empty ? <EmptyPerformanceChart label="Historical performance plot" height="100%" /> : !combined.length || (currentAttribution && ![showActualTotal, showActualFactor, showActualIdio, showAdjustment].some(Boolean)) ? historyLoading && currentAttribution ? null : <Typography color="text.secondary" sx={{ pt: 3 }}>
                    {historyLoading ? 'Loading return history…' : isSnapshotExposureHistory(actualPerformanceMetrics) ? actualPerformanceMetrics?.capabilities?.factor_payoffs?.available === true ? 'Portfolio contributions are unavailable. Inspect the retained factor payoffs and selected snapshot’s exposures above.' : 'No retained factor-return observations for this window. Inspect the selected snapshot’s exposures above.' : 'No return history to display for this window.'}
                </Typography> : <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={combined}
                        margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                        stackOffset="sign"
                        barCategoryGap="5%"
                        barGap={2}
                        onMouseDown={handleChartMouseDown}
                        onMouseMove={handleChartMouseMove}
                        onMouseUp={handleChartMouseUp}
                        onMouseLeave={handleChartMouseLeave}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={chartChrome.grid} />
                        <XAxis
                            dataKey="date"
                            tick={{ fill: chartChrome.mutedText }}
                            axisLine={{ stroke: chartChrome.axis }}
                            tickLine={{ stroke: chartChrome.axis }}
                            tickFormatter={formatTick}
                        />
                        <YAxis
                            tickFormatter={(v) => valueMode === 'dollar' ? formatCurrencyTick(v) : (v == null || isNaN(v) ? '-' : `${(v * 100).toFixed(2)}%`)}
                            tick={{ fill: chartChrome.mutedText }} axisLine={{ stroke: chartChrome.axis }} tickLine={{ stroke: chartChrome.axis }}
                        />
                        <RechartsTooltip
                            content={(props) => (
                                <DecompositionTooltip
                                    {...props}
                                    valueMode={valueMode}
                                    formatLabel={formatTick}
                                    factorView={effectiveFactorView}
                                    theme={theme}
                                    attributionMode={currentAttribution}
                                    portfolioLabel={portfolioLabel} leftLabel={leftLabel} rightLabel={residualLabel}
                                    reportingCurrency={reportingCurrency}
                                    selectedFactors={selectedFactors}
                                    getFactorColor={getFactorColor}
                                    availableSeries={{
                                        actual_adjustment: showAdjustment, actual_factor: showActualFactor, actual_total: showActualTotal, actual_idio: showActualIdio,
                                    }}
                                />
                            )}
                            followCursor={{ x: true, y: true }}
                            offset={12}
                            allowEscapeViewBox={{ x: false, y: false }}
                            wrapperStyle={{ transition: 'none', pointerEvents: 'none' }}
                        />

                        <Legend wrapperStyle={{ color: chartChrome.mutedText }} />
                        <ReferenceLine y={0} stroke={chartChrome.reference} />
                        {showDataReadinessMarker ? (
                            <ReferenceLine
                                x={firstComputableDate}
                                stroke="rgba(250, 204, 21, 0.92)"
                                strokeDasharray="4 3"
                            />
                        ) : null}
                        {chartHighlightedRange?.start && chartHighlightedRange?.end && chartHighlightedRange.start !== chartHighlightedRange.end && (
                            <ReferenceArea
                                x1={chartHighlightedRange.start}
                                x2={chartHighlightedRange.end}
                                fill="rgba(250, 204, 21, 0.16)"
                                stroke="rgba(250, 204, 21, 0.75)"
                                strokeOpacity={0.9}
                            />
                        )}

                        {/* Actual */}
                        {effectiveFactorView === 'total' ? (
                            <>
                                {showActualFactor && <Bar dataKey="actual_factor" name={`${portfolioLabel} ${leftLabel}`} stackId={actualStackId} barSize={barSizeDynamic} isAnimationActive={false} fill="#60a5fa" fillOpacity={1.0} />}
                                {showActualFactor && combined.filter((row) => Number.isFinite(row.actual_factor)).length === 1 && <Line id="actual-factor-single-observation" dataKey="actual_factor" stroke="#60a5fa" dot legendType="none" tooltipType="none" isAnimationActive={false} />}
                                {showActualIdio && <Bar dataKey="actual_idio" name={`${portfolioLabel} ${residualLabel}`} stackId={actualStackId} barSize={barSizeDynamic} isAnimationActive={false} fill="#1d4ed8" fillOpacity={1.0} />}
                            </>
                        ) : (
                            <>
                                {showActualFactor && selectedFactorsOrderedBySign.map((f) => {
                                    const keyA = factorKey(f);
                                    return <Bar key={keyA} dataKey={keyA} name={`${portfolioLabel} ${f}`} stackId={actualStackId} barSize={barSizeDynamic} isAnimationActive={false} fill={getFactorColor(f)} fillOpacity={1.0} />;
                                })}
                                {showActualIdio && <Bar dataKey="actual_idio" name={`${portfolioLabel} ${residualLabel}`} stackId={actualStackId} barSize={barSizeDynamic} isAnimationActive={false} fill="#1d4ed8" fillOpacity={1.0} />}
                                {currentAttribution && showActualFactor && <Bar dataKey="actual_other_factors" name="Other factors" stackId={actualStackId} barSize={barSizeDynamic} isAnimationActive={false} fill="#78909c" />}
                                {showActualFactor && <Line type="monotone" dataKey="actual_factor_total" name={`${portfolioLabel} Factor (net)`} stroke="#93c5fd" strokeWidth={2} strokeDasharray="4 4" dot={combined.filter((row) => Number.isFinite(row.actual_factor_total)).length === 1} isAnimationActive={false} />}
                            </>
                        )}
                        {showAdjustment && <Bar dataKey="actual_adjustment" name="Vendor-return adjustment" stackId={actualStackId} barSize={barSizeDynamic} fill="#a78bfa" isAnimationActive={false} />}
                        {showActualTotal && <Line type="monotone" dataKey="actual_total" name={`${portfolioLabel} Total`} stroke="#bfdbfe" strokeWidth={2} dot={combined.filter((row) => Number.isFinite(row.actual_total)).length === 1} isAnimationActive={false} />}

                    </ComposedChart>
                </ResponsiveContainer>}
                {dragStartMarker ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: dragStartMarker.x,
                            top: dragStartMarker.y,
                            transform: 'translate(-50%, -120%)',
                            px: 0.7,
                            py: 0.35,
                            borderRadius: 1,
                            bgcolor: 'rgba(2, 8, 20, 0.95)',
                            border: '1px solid rgba(96, 165, 250, 0.7)',
                            color: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.45,
                            pointerEvents: 'none',
                            zIndex: 8,
                            fontSize: 11,
                            lineHeight: 1.1,
                            fontWeight: 700,
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.34)',
                            whiteSpace: 'nowrap',
                            ...performanceDragMarkerSx,
                        }}
                    >
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: '#60a5fa',
                                color: '#0b1020',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: 9,
                                fontWeight: 800,
                            }}
                        >
                            S
                        </Box>
                        {dragStartMarker.label}
                    </Box>
                ) : null}
                {dragCurrentMarker ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: dragCurrentMarker.x,
                            top: dragCurrentMarker.y,
                            transform: 'translate(-50%, -120%)',
                            px: 0.7,
                            py: 0.35,
                            borderRadius: 1,
                            bgcolor: 'rgba(2, 8, 20, 0.95)',
                            border: '1px solid rgba(250, 204, 21, 0.7)',
                            color: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.45,
                            pointerEvents: 'none',
                            zIndex: 9,
                            fontSize: 11,
                            lineHeight: 1.1,
                            fontWeight: 700,
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.34)',
                            whiteSpace: 'nowrap',
                            ...performanceDragMarkerSx,
                        }}
                    >
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: '#facc15',
                                color: '#0b1020',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: 9,
                                fontWeight: 800,
                            }}
                        >
                            E
                        </Box>
                        {dragCurrentMarker.label}
                    </Box>
                ) : null}
            </Box>
        </Paper>
    );
}
